// SignaturesTab.jsx — rich HTML sign-offs, mapped per brand and per mailbox.
//
// THE PASTE IS THE FEATURE. An operator already has a signature: it is in
// Outlook, it has a logo, a layout table and a booking link, and it took
// somebody an afternoon. Retyping it into a plain textarea loses all of that,
// so this captures what the clipboard ACTUALLY carries — `text/html`, the real
// markup — instead of the `text/plain` flattening a normal input would take.
//
// What the operator then sees is the CLEANED version, not their paste: the
// server strips Outlook's conditional comments, its Mso classes and anything
// executable, and reports what it removed. Showing the raw paste and cleaning
// it silently on save would mean the preview and the sent email disagree, which
// is exactly the class of bug the reminder preview just taught us.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get, post, globalPut, remove } from "../../api";

const scopeLabel = (sig, brandName, mailboxName) => {
  const b = sig.brand_id ? brandName(sig.brand_id) || 'a brand' : null;
  const m = sig.account_id ? mailboxName(sig.account_id) || 'a mailbox' : null;
  if (b && m) return `${b} · ${m}`;
  if (b) return `${b} — every mailbox`;
  if (m) return `${m} — any brand`;
  return sig.isDefault ? 'Company-wide default' : 'Company-wide';
};

// ───────────────────────────────────────────────────────────── the editor
// ───────────────────────────────────────────────────────────── the editor
//
// The box is a real editor, not a drop target that only understands paste.
// The first version captured onPaste and nothing else, which meant it LOOKED
// editable — you could type into it, delete a line, drag the logo out — and
// every one of those edits was silently discarded on save. A control that
// accepts input and throws it away is worse than a read-only one.
//
// So the contentEditable is UNCONTROLLED and is the source of truth: React
// writes into it only when content arrives from outside (opening an existing
// signature, a paste, an image swap), never on a keystroke, because rewriting
// innerHTML mid-typing throws the caret back to the start of the box.
function SignatureModal({ sig, brands, accounts, onClose, onSaved }) {
  const editing = !!(sig && sig._id);
  const [name, setName] = useState(sig?.name || 'Signature');
  const [brandId, setBrandId] = useState(sig?.brand_id || '');
  const [accountId, setAccountId] = useState(sig?.account_id ? String(sig.account_id) : '');
  const [isDefault, setIsDefault] = useState(!!sig?.isDefault);
  const [clean, setClean] = useState(sig?.html
    ? { html: sig.html, text: sig.text || '', warnings: sig.warnings || [], bytes: sig.bytes || 0 }
    : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [showSource, setShowSource] = useState(false);
  const [hasContent, setHasContent] = useState(!!sig?.html);

  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const importRef = useRef(null);
  // The <img> the operator clicked, if any. Set means "replace THIS one";
  // unset means "insert a new image at the caret".
  const replaceTargetRef = useRef(null);

  const readEditor = () => (editorRef.current ? editorRef.current.innerHTML : '');

  // Clean on the server and write the result back into the box. The SAME
  // sanitiser runs on save, so what is shown here is what will be sent — there
  // is no second cleaning step to disagree with. Images are published to a URL
  // in the same call, which is why the box shows real src attributes rather
  // than the inline copy that was pasted.
  const applyHtml = useCallback(async (raw, { intoEditor = true, quiet = false } = {}) => {
    if (!raw || !raw.trim()) { setClean(null); setHasContent(false); return; }
    if (!quiet) setBusy(true);
    setError('');
    try {
      const r = await post('/api/email/signatures/sanitize', { html: raw }, false);
      const d = (r && r.data) || null;
      setClean(d);
      setHasContent(!!(d && d.html));
      if (intoEditor && editorRef.current && d) editorRef.current.innerHTML = d.html || '';
    } catch (e) { setError((e && e.message) || 'Could not read that'); }
    finally { if (!quiet) setBusy(false); }
  }, []);

  // Seed the box once, on open.
  useEffect(() => {
    if (editorRef.current && sig?.html) editorRef.current.innerHTML = sig.html;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fileToDataUri = (file) => new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => resolve('');
    r.readAsDataURL(file);
  });

  /**
   * Put an image in — either replacing the one that was clicked, or inserting
   * a new one where the caret is.
   *
   * The bytes go in as a data: URI and come back as a hosted URL, because the
   * server publishes them: Gmail strips embedded images, so a signature that
   * kept them would look right here and arrive with a hole in it.
   */
  const insertImage = useCallback(async (file) => {
    if (!file || !/^image\//.test(file.type)) { setError('That is not an image file'); return; }
    const uri = await fileToDataUri(file);
    if (!uri) { setError('Could not read that image'); return; }

    const target = replaceTargetRef.current;
    if (target && editorRef.current && editorRef.current.contains(target)) {
      target.setAttribute('src', uri);
      setNote('Image replaced.');
    } else if (editorRef.current) {
      const img = document.createElement('img');
      img.setAttribute('src', uri);
      img.setAttribute('width', '120');
      img.setAttribute('alt', '');
      const sel = window.getSelection();
      if (sel && sel.rangeCount && editorRef.current.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.insertNode(img);
        range.collapse(false);
      } else {
        editorRef.current.appendChild(img);
      }
      setNote('Image added.');
    }
    replaceTargetRef.current = null;
    await applyHtml(readEditor());
  }, [applyHtml]);

  /**
   * Import the signature from the file the operator already has.
   *
   * The only reliable route for the images: pasting carries the layout, but
   * desktop Outlook keeps the pictures inside the message and puts a bare cid:
   * reference on the clipboard, so the bytes are not there to be rescued. They
   * are in the file.
   */
  const importFile = useCallback(async (file) => {
    setBusy(true); setError(''); setNote('');
    try {
      const b64 = await fileToDataUri(file);
      const r = await post('/api/email/signatures/import', { file: b64, brand_id: brandId || null }, false);
      const d = (r && r.data) || null;
      if (!d || !d.html) { setError('Nothing usable was found in that file'); return; }
      setClean(d);
      setHasContent(true);
      if (editorRef.current) editorRef.current.innerHTML = d.html;
      const st = d.stats || {};
      setNote(`Imported from ${st.source === 'docx' ? 'the Word document' : 'the signature folder'} — ${st.imagesPlaced || 0} of ${st.imagesInFile || 0} images came with it.`);
    } catch (e) { setError((e && e.message) || 'Could not read that file'); }
    finally { setBusy(false); }
  }, [brandId]);

  const onPaste = async (e) => {
    const cd = e.clipboardData;
    const rich = cd && cd.getData('text/html');
    const plain = cd && cd.getData('text/plain');

    // Image files on the clipboard, whether or not any markup came with them.
    const files = Array.from((cd && cd.items) || [])
      .filter((it) => it.kind === 'file' && /^image\//.test(it.type))
      .map((it) => it.getAsFile())
      .filter(Boolean);

    // AN IMAGE ON ITS OWN. Copying just a logo puts a file on the clipboard
    // and often no markup at all — the first version read the (empty) HTML,
    // found nothing, and returned, so pasting an image did nothing whatsoever.
    if (!rich && !plain && files.length) {
      e.preventDefault();
      await insertImage(files[0]);
      return;
    }

    let raw = rich || (plain ? plain.split('\n').map((l) => `<p>${l}</p>`).join('') : '');
    if (!raw) return;
    e.preventDefault();

    // THE LOGO IN A PASTED SIGNATURE. Desktop Outlook does not put a usable
    // image URL in the HTML it copies — it writes `cid:image001.png@…`, its own
    // internal reference, or a `file:///…/msohtmlclip/…` path on the machine
    // that copied it. Neither resolves anywhere else. But the image itself IS
    // on the clipboard, so its bytes are substituted for those references in
    // document order.
    try {
      if (files.length) {
        const uris = await Promise.all(files.map(fileToDataUri));
        let i = 0;
        raw = raw.replace(/src\s*=\s*(["'])((?:cid:|file:|blob:)[^"']*)\1/gi, (full, q) => {
          const uri = uris[i++];
          return uri ? `src=${q}${uri}${q}` : full;
        });
      }
    } catch (_) { /* the paste still lands, just without the images */ }

    await applyHtml(raw);
  };

  const onDrop = async (e) => {
    const file = Array.from(e.dataTransfer.files || []).find((f) => /^image\//.test(f.type));
    if (!file) return;
    e.preventDefault();
    // A drop lands where it is dropped, so treat whatever is under the pointer
    // as the thing being replaced.
    replaceTargetRef.current = e.target && e.target.tagName === 'IMG' ? e.target : null;
    await insertImage(file);
  };

  // Clicking an image is the natural way to say "this one". It arms the file
  // picker to REPLACE rather than insert.
  const onEditorClick = (e) => {
    if (e.target && e.target.tagName === 'IMG') {
      replaceTargetRef.current = e.target;
      setNote('Replacing that image — choose the new one.');
      if (fileRef.current) fileRef.current.click();
    }
  };

  // Typing, deleting, dragging things around inside the box. Recomputed for
  // the counters WITHOUT writing back into the editor, which would move the
  // caret to the start on every keystroke.
  const inputTimer = useRef(null);
  const onInput = () => {
    setHasContent(!!readEditor().trim());
    if (inputTimer.current) clearTimeout(inputTimer.current);
    inputTimer.current = setTimeout(() => applyHtml(readEditor(), { intoEditor: false, quiet: true }), 700);
  };

  const submit = async () => {
    setError('');
    // Read the BOX, not a state variable — manual edits live in the DOM.
    const current = readEditor();
    if (!current.trim()) { setError('Paste or write your signature first'); return; }
    setBusy(true);
    try {
      const body = { name, brand_id: brandId || null, account_id: accountId || null, isDefault, html: current };
      if (editing) await globalPut(`/api/email/signatures/${sig._id}`, body, false);
      else await post('/api/email/signatures', body, false);
      onSaved();
    } catch (e) { setError((e && e.message) || 'Could not save the signature'); }
    finally { setBusy(false); }
  };

  const mailboxes = useMemo(
    () => (accounts || []).filter((a) => !brandId || !a.brand_id || String(a.brand_id) === String(brandId)),
    [accounts, brandId],
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 860, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">{editing ? 'Edit signature' : 'New signature'}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>

        {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}
        {note && <div className="alert alert-info" style={{ fontSize: 12, padding: '6px 10px' }}>{note}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Name
            <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="Accounts — BidLight" />
          </label>
          <label style={{ fontSize: 12, color: '#666' }}>Brand
            <select className="form-control" value={brandId} onChange={(e) => { setBrandId(e.target.value); setAccountId(''); }}>
              <option value="">Any brand</option>
              {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name}</option>))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#666' }}>Mailbox
            <select className="form-control" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Every mailbox of that brand</option>
              {mailboxes.map((a) => (<option key={a._id} value={a._id}>{a.fromAddress || a.name}</option>))}
            </select>
          </label>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
          The most specific match wins: this brand and mailbox, then the brand, then the mailbox, then the company default.
        </div>

        <label style={{ fontSize: 13, marginTop: 12, display: 'block' }}>
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />{' '}
          Use as the company-wide default when nothing more specific matches
        </label>

        <div className="d-flex align-items-baseline justify-content-between" style={{ marginTop: 16 }}>
          <h6 style={{ fontSize: 13, margin: 0 }}>Your signature</h6>
          <span>
            <button
              className="acc-btn"
              style={{ fontSize: 11, padding: '2px 10px' }}
              onClick={() => importRef.current && importRef.current.click()}
              title="Your Outlook signature folder zipped, or the Word document you keep it in"
            >Import from file</button>
            <button
              className="acc-btn ms-1"
              style={{ fontSize: 11, padding: '2px 10px' }}
              onClick={() => { replaceTargetRef.current = null; if (fileRef.current) fileRef.current.click(); }}
            >+ Add image</button>
            {hasContent && (
              <button
                className="acc-btn ms-1"
                style={{ fontSize: 11, padding: '2px 10px' }}
                onClick={() => { if (editorRef.current) editorRef.current.innerHTML = ''; setClean(null); setHasContent(false); setNote(''); }}
              >Clear</button>
            )}
          </span>
        </div>
        <p style={{ fontSize: 12, color: '#6b7a86', margin: '6px 0' }}>
          Paste it straight out of Outlook (or Gmail, or Apple Mail) — tables, links, colours and
          formatting all come with it. <b>Images cannot travel on the clipboard</b>: Outlook keeps
          them inside the message, so they arrive as dashed placeholders you click to fill.
          <b> Import from file</b> avoids that entirely — give it your Word document, or your
          Outlook signature folder zipped (<code>%APPDATA%\Microsoft\Signatures</code>), and the
          logo and icons come with it. You can also type in the box, click any image to swap it,
          or drag one in. Images are published to a web address automatically, because embedded
          ones are stripped by Gmail.
        </p>

        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onPaste={onPaste}
          onInput={onInput}
          onClick={onEditorClick}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          data-placeholder="Paste your signature here…"
          style={{
            border: '2px dashed #cfd6dc', borderRadius: 8, padding: 14, minHeight: 110,
            background: '#fcfdfe', fontSize: 13, outline: 'none',
          }}
        />
        {!hasContent && (
          <div style={{ fontSize: 12, color: '#9aa4ad', marginTop: -34, marginLeft: 16, pointerEvents: 'none', height: 22 }}>
            Paste your signature here…
          </div>
        )}

        <input
          ref={importRef}
          type="file"
          accept=".docx,.zip,application/zip,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const f = e.target.files && e.target.files[0];
            e.target.value = '';
            if (f) await importFile(f);
          }}
        />

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const f = e.target.files && e.target.files[0];
            e.target.value = '';           // so choosing the same file twice still fires
            if (f) await insertImage(f);
          }}
        />

        {busy && <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Working…</div>}

        {clean && (
          <>
            {(clean.warnings || []).map((w, i) => (
              <div key={i} className="alert alert-warning" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>{w}</div>
            ))}
            <div style={{ fontSize: 11, color: '#888', marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span>{Math.max(1, Math.round((clean.bytes || 0) / 1024))} KB — added to every email this signs</span>
              <button className="acc-btn" style={{ fontSize: 11, padding: '1px 8px' }} onClick={() => setShowSource((v) => !v)}>
                {showSource ? 'Hide' : 'Show'} the cleaned HTML
              </button>
            </div>
            {showSource && (
              <pre style={{ background: '#f7f8f9', border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, fontSize: 11, maxHeight: 220, overflow: 'auto', marginTop: 8 }}>
                {clean.html}
              </pre>
            )}
            {clean.text && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7a86', marginBottom: 4 }}>
                  The plain-text half, for recipients whose client shows that part:
                </div>
                <pre style={{ background: '#f7f8f9', border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, fontSize: 11, whiteSpace: 'pre-wrap', margin: 0 }}>{clean.text}</pre>
              </div>
            )}
          </>
        )}

        <div style={{ fontSize: 11, color: '#888', marginTop: 12 }}>
          Tokens still work inside a pasted signature: <code>{'{{ownerName}}'}</code>,{' '}
          <code>{'{{ownerTitle}}'}</code>, <code>{'{{ownerPhone}}'}</code>, <code>{'{{brandName}}'}</code>,{' '}
          <code>{'{{companyWebsite}}'}</code>.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy || !hasContent}>
            {busy ? 'Saving…' : (editing ? 'Save signature' : 'Add signature')}
          </button>
        </div>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────── the tab
export default function SignaturesTab({ brands, canEdit }) {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [editingSig, setEditingSig] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const [s, a] = await Promise.all([
        get('/api/email/signatures'),
        get('/api/email/accounts').catch(() => null),
      ]);
      setRows((s && s.data) || []);
      setAccounts((a && a.data) || []);
    } catch (e) { setErr((e && e.message) || 'Could not load signatures'); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const brandName = useCallback((id) => {
    const b = (brands || []).find((x) => String(x._id || x.id) === String(id));
    return (b && b.name) || '';
  }, [brands]);
  const mailboxName = useCallback((id) => {
    const a = accounts.find((x) => String(x._id) === String(id));
    return (a && (a.fromAddress || a.name)) || '';
  }, [accounts]);

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Emails it signs will fall back to the next matching signature.`)) return;
    try { await remove(`/api/email/signatures/${row._id}`); await load(); }
    catch (e) { setErr((e && e.message) || 'Could not delete it'); }
  };

  return (
    <div>
      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <div style={{ fontSize: 12, color: '#6b7a86' }}>
          Appended to every email this brand or mailbox sends — invoices, sequences, campaigns,
          replies, all of them.
        </div>
        {canEdit && <button className="acc-btn acc-btn-primary" onClick={() => setEditingSig({})}>+ New signature</button>}
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {busy && rows.length === 0 && <div className="em-empty">Loading…</div>}
      {!busy && rows.length === 0 && (
        <div className="em-empty">
          No signatures yet. Copy the one you already use in Outlook and paste it in — the logo,
          the links and the layout come with it.
        </div>
      )}

      {rows.length > 0 && (
        <table className="em-table">
          <thead><tr><th>Name</th><th>Applies to</th><th>Preview</th><th>Size</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id}>
                <td>
                  {r.name}
                  {r.isDefault && <span style={{ fontSize: 10, background: '#eef2ff', color: '#15172b', borderRadius: 10, padding: '1px 7px', marginLeft: 6 }}>default</span>}
                  {r.active === false && <span style={{ fontSize: 10, color: '#a15c00', marginLeft: 6 }}>off</span>}
                </td>
                <td style={{ fontSize: 12 }}>{scopeLabel(r, brandName, mailboxName)}</td>
                <td>
                  <div style={{ maxHeight: 64, overflow: 'hidden', fontSize: 11, border: '1px solid #f1f1f1', borderRadius: 6, padding: 6, background: '#fff' }}
                    dangerouslySetInnerHTML={{ __html: r.html || '' }} />
                </td>
                <td style={{ fontSize: 12 }}>{Math.max(1, Math.round((r.bytes || 0) / 1024))} KB</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit && <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setEditingSig(r)}>Edit</button>}
                  {canEdit && <button className="acc-btn ms-1" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => del(r)}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editingSig && (
        <SignatureModal
          sig={editingSig._id ? editingSig : null}
          brands={brands}
          accounts={accounts}
          onClose={() => setEditingSig(null)}
          onSaved={() => { setEditingSig(null); load(); }}
        />
      )}
    </div>
  );
}

export { scopeLabel };
